import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, type UseInfiniteQueryOptions, type UseQueryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { clinicClock } from "@/lib/clinic-clock";
import type { Page } from "@/types/pagination";
import { type TaskCommentDetail, type TaskCommentUpdate, type TaskCreate, type TaskDetail, type TaskFetchParams, type TaskLogDetail, TaskPriority, TaskStatus, type TaskSummary, type TaskUpdate } from "@/types/task";
import { addComment, completeTask, createTask, deleteComment, deleteTask, fetchComments, fetchTask, fetchTaskActivity, fetchTaskCounts, fetchTasks, fetchTasksExist, setTaskSubscribers, subscribeToTask, unsubscribeFromTask, updateComment, updateTask } from "./tasks";
import { callQueryKeys } from "@/api/provider/comms/call";
import { canonicalFilterKey } from "@/features/table-filters/contract/state";
export const taskQueryKeys = {
  all: ["tasks"] as const,
  lists: () => [...taskQueryKeys.all, "list"] as const,
  list: (filters: TaskFetchParams) => [...taskQueryKeys.lists(), canonicalFilterKey(filters)] as const,
  counts: () => [...taskQueryKeys.all, "counts"] as const,
  count: (context: {
    tenantSubDomain?: string;
    providerId?: string;
    taskDefaultFilter?: string;
    locationIds?: string[];
    assigneeId?: string;
    subscriberId?: string;
    allTasks?: boolean;
  }) => [...taskQueryKeys.counts(), context] as const,
  scopedCounts: (params: {
    assigneeId?: string;
    subscriberId?: string;
    locationIds?: string[];
  }) => [...taskQueryKeys.counts(), "scoped", params] as const,
  exists: () => [...taskQueryKeys.all, "exists"] as const,
  details: () => [...taskQueryKeys.all, "detail"] as const,
  detail: (taskId: string) => [...taskQueryKeys.details(), taskId] as const,
  comments: (taskId: string) => [...taskQueryKeys.all, "comments", taskId] as const,
  activity: (taskId: string) => [...taskQueryKeys.all, "activity", taskId] as const
};
type TaskQueryOptions<TQueryFnData, TData = TQueryFnData, TOmitKeys extends string = "queryKey" | "queryFn"> = Omit<UseQueryOptions<TQueryFnData, Error, TData, readonly unknown[]>, TOmitKeys>;
const EMPTY_PAGE: Page<TaskSummary> = {
  data: [],
  total: 0
};
const EMPTY_COMMENTS: TaskCommentDetail[] = [];
const EMPTY_ACTIVITY: TaskLogDetail[] = [];
export const taskQueryOptions = {
  list: (filters: TaskFetchParams) => ({
    queryKey: taskQueryKeys.list(filters),
    queryFn: () => fetchTasks(filters),
    select: (data: Page<TaskSummary> | null) => data ?? EMPTY_PAGE
  }),
  detail: (taskId: string) => ({
    queryKey: taskQueryKeys.detail(taskId),
    queryFn: () => fetchTask(taskId),
    enabled: !!taskId
  }),
  comments: (taskId: string) => ({
    queryKey: taskQueryKeys.comments(taskId),
    queryFn: () => fetchComments(taskId),
    enabled: !!taskId,
    select: (data: TaskCommentDetail[] | null) => data ?? EMPTY_COMMENTS
  }),
  activity: (taskId: string) => ({
    queryKey: taskQueryKeys.activity(taskId),
    queryFn: () => fetchTaskActivity(taskId),
    enabled: !!taskId,
    select: (data: TaskLogDetail[] | null) => data ?? EMPTY_ACTIVITY
  })
};
export function useTasks(filters, options) {
  const $ = _c(5);
  let t0;
  if ($[0] !== filters) {
    t0 = taskQueryOptions.list(filters);
    $[0] = filters;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      ...t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
}
export function useTasksExist() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: taskQueryKeys.exists(),
      queryFn: fetchTasksExist
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
}
const PANEL_TASKS_PER_PAGE = 20;
type TasksInfiniteOptions = Omit<UseInfiniteQueryOptions<Page<TaskSummary>, Error, InfiniteData<Page<TaskSummary>, number>, readonly unknown[], number>, "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"> & {
  perPage?: number;
};

/**
 * Paginated task list for sidebars / infinite scroll (flattened pages).
 */
export function useTasksInfinite(filters: Omit<TaskFetchParams, "page" | "perPage">, options?: TasksInfiniteOptions & {
  perPage?: number;
}) {
  const {
    perPage: perPageOption,
    ...infiniteQueryOptions
  } = options ?? {};
  const perPage = perPageOption ?? PANEL_TASKS_PER_PAGE;
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: [...taskQueryKeys.lists(), "infinite", {
      ...filters,
      perPage
    }] as const,
    queryFn: async ({
      pageParam = 1
    }) => {
      const response = await fetchTasks({
        ...filters,
        page: pageParam,
        perPage
      });
      return response ?? EMPTY_PAGE;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.data.length, 0);
      if (loadedCount < lastPage.total) {
        return allPages.length + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    ...infiniteQueryOptions
  });
  const tasks = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page_0 => page_0.data);
  }, [data?.pages]);
  const totalTasks = data?.pages?.[0]?.total ?? 0;
  const handleFetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  return {
    tasks,
    isLoading,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage: handleFetchNextPage,
    refetch,
    totalTasks
  };
}
export function useTask(taskId, options) {
  const $ = _c(5);
  let t0;
  if ($[0] !== taskId) {
    t0 = taskQueryOptions.detail(taskId);
    $[0] = taskId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      ...t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
}
export function useTaskComments(taskId, options) {
  const $ = _c(5);
  let t0;
  if ($[0] !== taskId) {
    t0 = taskQueryOptions.comments(taskId);
    $[0] = taskId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      ...t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
}
export function useTaskActivity(taskId, options) {
  const $ = _c(5);
  let t0;
  if ($[0] !== taskId) {
    t0 = taskQueryOptions.activity(taskId);
    $[0] = taskId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      ...t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
}
export function useTaskCounts(t0, options) {
  const $ = _c(17);
  const enabled = t0 === undefined ? true : t0;
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  const locationIds = options?.locationIds;
  const assigneeId = options?.assigneeId;
  const subscriberId = options?.subscriberId;
  const allTasks = options?.allTasks;
  let t1;
  if ($[0] !== allTasks || $[1] !== assigneeId || $[2] !== currentUser?.id || $[3] !== currentUser?.taskDefaultFilter || $[4] !== locationIds || $[5] !== subscriberId || $[6] !== tenant?.subDomain) {
    t1 = taskQueryKeys.count({
      tenantSubDomain: tenant?.subDomain,
      providerId: currentUser?.id,
      taskDefaultFilter: currentUser?.taskDefaultFilter,
      locationIds,
      assigneeId,
      subscriberId,
      allTasks
    });
    $[0] = allTasks;
    $[1] = assigneeId;
    $[2] = currentUser?.id;
    $[3] = currentUser?.taskDefaultFilter;
    $[4] = locationIds;
    $[5] = subscriberId;
    $[6] = tenant?.subDomain;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== allTasks || $[9] !== assigneeId || $[10] !== locationIds || $[11] !== subscriberId) {
    t2 = () => fetchTaskCounts({
      locationIds,
      assigneeId,
      subscriberId,
      allTasks
    });
    $[8] = allTasks;
    $[9] = assigneeId;
    $[10] = locationIds;
    $[11] = subscriberId;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== enabled || $[14] !== t1 || $[15] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled,
      refetchInterval: 300000
    };
    $[13] = enabled;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return useQuery(t3);
}
export const useTaskMutations = () => {
  const $ = _c(138);
  const queryClient = useQueryClient();
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] !== queryClient) {
    t0 = () => queryClient.getQueriesData({
      queryKey: taskQueryKeys.lists()
    }).filter(_temp4);
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getPagedListQueries = t0;
  let t1;
  if ($[2] !== queryClient) {
    t1 = () => queryClient.getQueriesData({
      queryKey: taskQueryKeys.lists()
    }).filter(_temp5);
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getInfiniteListQueries = t1;
  let t2;
  if ($[4] !== getInfiniteListQueries || $[5] !== queryClient) {
    t2 = taskId => {
      const previous = getInfiniteListQueries();
      previous.forEach(t3 => {
        const [queryKey, data] = t3;
        if (!data) {
          return;
        }
        queryClient.setQueryData(queryKey, {
          ...data,
          pages: data.pages.map(page => ({
            ...page,
            data: page.data.filter(t => t.id !== taskId),
            total: Math.max(0, page.total - 1)
          }))
        });
      });
      return previous;
    };
    $[4] = getInfiniteListQueries;
    $[5] = queryClient;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const removeFromInfiniteLists = t2;
  let t3;
  if ($[7] !== getInfiniteListQueries || $[8] !== queryClient) {
    t3 = (taskId_0, update) => {
      const previous_0 = getInfiniteListQueries();
      previous_0.forEach(t4 => {
        const [queryKey_0, data_0] = t4;
        if (!data_0) {
          return;
        }
        queryClient.setQueryData(queryKey_0, {
          ...data_0,
          pages: data_0.pages.map(page_0 => ({
            ...page_0,
            data: page_0.data.map(t_0 => t_0.id === taskId_0 ? update(t_0) : t_0)
          }))
        });
      });
      return previous_0;
    };
    $[7] = getInfiniteListQueries;
    $[8] = queryClient;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const patchInfiniteLists = t3;
  let t4;
  if ($[10] !== queryClient) {
    t4 = () => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.counts()
      });
      queryClient.invalidateQueries({
        queryKey: callQueryKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.exists()
      });
    };
    $[10] = queryClient;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const invalidateTasks = t4;
  let t5;
  if ($[12] !== queryClient) {
    t5 = taskId_1 => {
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.detail(taskId_1)
      });
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.comments(taskId_1)
      });
      queryClient.invalidateQueries({
        queryKey: taskQueryKeys.activity(taskId_1)
      });
    };
    $[12] = queryClient;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const invalidateTask = t5;
  let t6;
  if ($[14] !== currentUser || $[15] !== getPagedListQueries || $[16] !== queryClient) {
    t6 = async newTask => {
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.lists()
      });
      const previousLists = getPagedListQueries();
      const optimisticTask = {
        id: `temp-${Date.now()}`,
        title: newTask.title,
        description: newTask.description,
        status: newTask.status ?? TaskStatus.OPEN,
        priority: newTask.priority ?? TaskPriority.NORMAL,
        startDate: newTask.startDate || (newTask.status === TaskStatus.IN_PROGRESS ? clinicClock().today() : undefined),
        dueDate: newTask.dueDate,
        patientId: newTask.patientId,
        assigneeId: newTask.assigneeId,
        creatorId: currentUser?.id ?? "",
        createdDate: new Date().toISOString(),
        creator: currentUser ? {
          id: currentUser.id,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName
        } : undefined
      };
      previousLists.forEach(t7 => {
        const [queryKey_1, data_1] = t7;
        if (data_1) {
          queryClient.setQueryData(queryKey_1, {
            ...data_1,
            data: [optimisticTask, ...data_1.data],
            total: data_1.total + 1
          });
        }
      });
      return {
        previousLists
      };
    };
    $[14] = currentUser;
    $[15] = getPagedListQueries;
    $[16] = queryClient;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== queryClient) {
    t7 = (_, __, context) => {
      context?.previousLists.forEach(t8 => {
        const [queryKey_2, data_2] = t8;
        queryClient.setQueryData(queryKey_2, data_2);
      });
    };
    $[18] = queryClient;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== invalidateTasks) {
    t8 = () => {
      invalidateTasks();
    };
    $[20] = invalidateTasks;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = {
      mutationFn: _temp6,
      onMutate: t6,
      onError: t7,
      onSettled: t8
    };
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const t10 = useMutation(t9);
  let t11;
  if ($[26] !== getPagedListQueries || $[27] !== patchInfiniteLists || $[28] !== queryClient) {
    t11 = async t12 => {
      const {
        taskId: taskId_3,
        task: task_1
      } = t12;
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.lists()
      });
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.detail(taskId_3)
      });
      const previousLists_0 = getPagedListQueries();
      const previousDetail = queryClient.getQueryData(taskQueryKeys.detail(taskId_3));
      const optimisticUpdate = {
        ...task_1
      };
      if (task_1.status === TaskStatus.IN_PROGRESS && !task_1.startDate && previousDetail?.status !== TaskStatus.IN_PROGRESS) {
        optimisticUpdate.startDate = clinicClock().today();
      }
      const previousInfinite = patchInfiniteLists(taskId_3, t_1 => ({
        ...t_1,
        ...optimisticUpdate
      }));
      previousLists_0.forEach(t13 => {
        const [queryKey_3, data_3] = t13;
        if (data_3) {
          queryClient.setQueryData(queryKey_3, {
            ...data_3,
            data: data_3.data.map(t_2 => t_2.id === taskId_3 ? {
              ...t_2,
              ...optimisticUpdate
            } : t_2)
          });
        }
      });
      if (previousDetail) {
        queryClient.setQueryData(taskQueryKeys.detail(taskId_3), {
          ...previousDetail,
          ...optimisticUpdate
        });
      }
      return {
        previousLists: previousLists_0,
        previousInfinite,
        previousDetail
      };
    };
    $[26] = getPagedListQueries;
    $[27] = patchInfiniteLists;
    $[28] = queryClient;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== queryClient) {
    t12 = (__0, variables, context_0) => {
      context_0?.previousLists.forEach(t13 => {
        const [queryKey_4, data_4] = t13;
        queryClient.setQueryData(queryKey_4, data_4);
      });
      context_0?.previousInfinite.forEach(t14 => {
        const [queryKey_5, data_5] = t14;
        queryClient.setQueryData(queryKey_5, data_5);
      });
      if (context_0?.previousDetail) {
        queryClient.setQueryData(taskQueryKeys.detail(variables.taskId), context_0.previousDetail);
      }
    };
    $[30] = queryClient;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== invalidateTask || $[33] !== invalidateTasks) {
    t13 = (__1, ___0, variables_0) => {
      invalidateTasks();
      invalidateTask(variables_0.taskId);
    };
    $[32] = invalidateTask;
    $[33] = invalidateTasks;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t13) {
    t14 = {
      mutationFn: _temp7,
      onMutate: t11,
      onError: t12,
      onSettled: t13
    };
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const t15 = useMutation(t14);
  let t16;
  if ($[39] !== getPagedListQueries || $[40] !== queryClient || $[41] !== removeFromInfiniteLists) {
    t16 = async taskId_5 => {
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.lists()
      });
      const previousLists_1 = getPagedListQueries();
      const previousInfinite_0 = removeFromInfiniteLists(taskId_5);
      previousLists_1.forEach(t17 => {
        const [queryKey_6, data_6] = t17;
        if (data_6) {
          queryClient.setQueryData(queryKey_6, {
            ...data_6,
            data: data_6.data.filter(t_3 => t_3.id !== taskId_5),
            total: data_6.total - 1
          });
        }
      });
      return {
        previousLists: previousLists_1,
        previousInfinite: previousInfinite_0
      };
    };
    $[39] = getPagedListQueries;
    $[40] = queryClient;
    $[41] = removeFromInfiniteLists;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== queryClient) {
    t17 = (__2, ___1, context_1) => {
      context_1?.previousLists.forEach(t18 => {
        const [queryKey_7, data_7] = t18;
        queryClient.setQueryData(queryKey_7, data_7);
      });
      context_1?.previousInfinite.forEach(t19 => {
        const [queryKey_8, data_8] = t19;
        queryClient.setQueryData(queryKey_8, data_8);
      });
    };
    $[43] = queryClient;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== invalidateTask || $[46] !== invalidateTasks) {
    t18 = (__3, ___2, taskId_6) => {
      invalidateTasks();
      invalidateTask(taskId_6);
    };
    $[45] = invalidateTask;
    $[46] = invalidateTasks;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t16 || $[49] !== t17 || $[50] !== t18) {
    t19 = {
      mutationFn: _temp8,
      onMutate: t16,
      onError: t17,
      onSettled: t18
    };
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const t20 = useMutation(t19);
  let t21;
  if ($[52] !== currentUser?.id || $[53] !== getPagedListQueries || $[54] !== patchInfiniteLists || $[55] !== queryClient) {
    t21 = async taskId_8 => {
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.lists()
      });
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.detail(taskId_8)
      });
      const previousLists_2 = getPagedListQueries();
      const previousDetail_0 = queryClient.getQueryData(taskQueryKeys.detail(taskId_8));
      const completedDate = new Date().toISOString();
      const completedById = currentUser?.id;
      const previousInfinite_1 = patchInfiniteLists(taskId_8, t_4 => ({
        ...t_4,
        status: TaskStatus.COMPLETED,
        completedDate,
        completedById
      }));
      previousLists_2.forEach(t22 => {
        const [queryKey_9, data_9] = t22;
        if (data_9) {
          queryClient.setQueryData(queryKey_9, {
            ...data_9,
            data: data_9.data.map(t_5 => t_5.id === taskId_8 ? {
              ...t_5,
              status: TaskStatus.COMPLETED,
              completedDate,
              completedById
            } : t_5)
          });
        }
      });
      if (previousDetail_0) {
        queryClient.setQueryData(taskQueryKeys.detail(taskId_8), {
          ...previousDetail_0,
          status: TaskStatus.COMPLETED,
          completedDate,
          completedById
        });
      }
      return {
        previousLists: previousLists_2,
        previousInfinite: previousInfinite_1,
        previousDetail: previousDetail_0
      };
    };
    $[52] = currentUser?.id;
    $[53] = getPagedListQueries;
    $[54] = patchInfiniteLists;
    $[55] = queryClient;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== queryClient) {
    t22 = (__4, taskId_9, context_2) => {
      context_2?.previousLists.forEach(t23 => {
        const [queryKey_10, data_10] = t23;
        queryClient.setQueryData(queryKey_10, data_10);
      });
      context_2?.previousInfinite.forEach(t24 => {
        const [queryKey_11, data_11] = t24;
        queryClient.setQueryData(queryKey_11, data_11);
      });
      if (context_2?.previousDetail) {
        queryClient.setQueryData(taskQueryKeys.detail(taskId_9), context_2.previousDetail);
      }
    };
    $[57] = queryClient;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== invalidateTask || $[60] !== invalidateTasks) {
    t23 = (__5, ___3, taskId_10) => {
      invalidateTasks();
      invalidateTask(taskId_10);
    };
    $[59] = invalidateTask;
    $[60] = invalidateTasks;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t21 || $[63] !== t22 || $[64] !== t23) {
    t24 = {
      mutationFn: _temp9,
      onMutate: t21,
      onError: t22,
      onSettled: t23
    };
    $[62] = t21;
    $[63] = t22;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  const t25 = useMutation(t24);
  let t26;
  if ($[66] !== currentUser || $[67] !== queryClient) {
    t26 = async t27 => {
      const {
        taskId: taskId_12,
        content: content_0,
        files: files_0
      } = t27;
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.detail(taskId_12)
      });
      const previousTask = queryClient.getQueryData(taskQueryKeys.detail(taskId_12));
      if (previousTask) {
        const optimisticComment = {
          id: `temp-${Date.now()}`,
          taskId: taskId_12,
          authorId: currentUser?.id ?? "",
          content: content_0,
          attachments: files_0?.map(_temp11),
          createdDate: new Date().toISOString(),
          author: currentUser ? {
            id: currentUser.id,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName
          } : undefined
        };
        queryClient.setQueryData(taskQueryKeys.detail(taskId_12), {
          ...previousTask,
          comments: [...previousTask.comments, optimisticComment]
        });
      }
      return {
        previousTask
      };
    };
    $[66] = currentUser;
    $[67] = queryClient;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== queryClient) {
    t27 = (__6, variables_1, context_3) => {
      if (context_3?.previousTask) {
        queryClient.setQueryData(taskQueryKeys.detail(variables_1.taskId), context_3.previousTask);
      }
    };
    $[69] = queryClient;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== invalidateTask) {
    t28 = (__7, ___4, variables_2) => {
      invalidateTask(variables_2.taskId);
    };
    $[71] = invalidateTask;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== t26 || $[74] !== t27 || $[75] !== t28) {
    t29 = {
      mutationFn: _temp10,
      onMutate: t26,
      onError: t27,
      onSettled: t28
    };
    $[73] = t26;
    $[74] = t27;
    $[75] = t28;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  const t30 = useMutation(t29);
  let t31;
  let t32;
  if ($[77] !== queryClient) {
    t31 = async t33 => {
      const {
        taskId: taskId_14,
        commentId: commentId_0,
        comment: comment_0
      } = t33;
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.detail(taskId_14)
      });
      const previousTask_0 = queryClient.getQueryData(taskQueryKeys.detail(taskId_14));
      if (previousTask_0) {
        queryClient.setQueryData(taskQueryKeys.detail(taskId_14), {
          ...previousTask_0,
          comments: previousTask_0.comments.map(c => c.id === commentId_0 ? {
            ...c,
            content: comment_0.content,
            updatedDate: new Date().toISOString()
          } : c)
        });
      }
      return {
        previousTask: previousTask_0
      };
    };
    t32 = (__8, variables_3, context_4) => {
      if (context_4?.previousTask) {
        queryClient.setQueryData(taskQueryKeys.detail(variables_3.taskId), context_4.previousTask);
      }
    };
    $[77] = queryClient;
    $[78] = t31;
    $[79] = t32;
  } else {
    t31 = $[78];
    t32 = $[79];
  }
  let t33;
  if ($[80] !== invalidateTask) {
    t33 = (__9, ___5, variables_4) => {
      invalidateTask(variables_4.taskId);
    };
    $[80] = invalidateTask;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== t31 || $[83] !== t32 || $[84] !== t33) {
    t34 = {
      mutationFn: _temp12,
      onMutate: t31,
      onError: t32,
      onSettled: t33
    };
    $[82] = t31;
    $[83] = t32;
    $[84] = t33;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  const t35 = useMutation(t34);
  let t36;
  let t37;
  if ($[86] !== queryClient) {
    t36 = async t38 => {
      const {
        taskId: taskId_16,
        commentId: commentId_2
      } = t38;
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.detail(taskId_16)
      });
      const previousTask_1 = queryClient.getQueryData(taskQueryKeys.detail(taskId_16));
      if (previousTask_1) {
        queryClient.setQueryData(taskQueryKeys.detail(taskId_16), {
          ...previousTask_1,
          comments: previousTask_1.comments.filter(c_0 => c_0.id !== commentId_2)
        });
      }
      return {
        previousTask: previousTask_1
      };
    };
    t37 = (__10, variables_5, context_5) => {
      if (context_5?.previousTask) {
        queryClient.setQueryData(taskQueryKeys.detail(variables_5.taskId), context_5.previousTask);
      }
    };
    $[86] = queryClient;
    $[87] = t36;
    $[88] = t37;
  } else {
    t36 = $[87];
    t37 = $[88];
  }
  let t38;
  if ($[89] !== invalidateTask) {
    t38 = (__11, ___6, variables_6) => {
      invalidateTask(variables_6.taskId);
    };
    $[89] = invalidateTask;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  let t39;
  if ($[91] !== t36 || $[92] !== t37 || $[93] !== t38) {
    t39 = {
      mutationFn: _temp13,
      onMutate: t36,
      onError: t37,
      onSettled: t38
    };
    $[91] = t36;
    $[92] = t37;
    $[93] = t38;
    $[94] = t39;
  } else {
    t39 = $[94];
  }
  const t40 = useMutation(t39);
  let t41;
  if ($[95] !== currentUser || $[96] !== queryClient) {
    t41 = async taskId_18 => {
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.detail(taskId_18)
      });
      const previousDetail_1 = queryClient.getQueryData(taskQueryKeys.detail(taskId_18));
      if (previousDetail_1) {
        queryClient.setQueryData(taskQueryKeys.detail(taskId_18), {
          ...previousDetail_1,
          subscribers: [...previousDetail_1.subscribers, {
            id: `temp-${Date.now()}`,
            providerId: currentUser?.id ?? "",
            createdDate: new Date().toISOString(),
            provider: currentUser ? {
              id: currentUser.id,
              firstName: currentUser.firstName,
              lastName: currentUser.lastName
            } : undefined
          }]
        });
      }
      return {
        previousDetail: previousDetail_1
      };
    };
    $[95] = currentUser;
    $[96] = queryClient;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  let t42;
  if ($[98] !== queryClient) {
    t42 = (__12, taskId_19, context_6) => {
      if (context_6?.previousDetail) {
        queryClient.setQueryData(taskQueryKeys.detail(taskId_19), context_6.previousDetail);
      }
    };
    $[98] = queryClient;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== invalidateTask) {
    t43 = (__13, ___7, taskId_20) => {
      invalidateTask(taskId_20);
    };
    $[100] = invalidateTask;
    $[101] = t43;
  } else {
    t43 = $[101];
  }
  let t44;
  if ($[102] !== t41 || $[103] !== t42 || $[104] !== t43) {
    t44 = {
      mutationFn: _temp14,
      onMutate: t41,
      onError: t42,
      onSettled: t43
    };
    $[102] = t41;
    $[103] = t42;
    $[104] = t43;
    $[105] = t44;
  } else {
    t44 = $[105];
  }
  const t45 = useMutation(t44);
  let t46;
  if ($[106] !== currentUser?.id || $[107] !== queryClient) {
    t46 = async taskId_22 => {
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.detail(taskId_22)
      });
      const previousDetail_2 = queryClient.getQueryData(taskQueryKeys.detail(taskId_22));
      if (previousDetail_2) {
        queryClient.setQueryData(taskQueryKeys.detail(taskId_22), {
          ...previousDetail_2,
          subscribers: previousDetail_2.subscribers.filter(s => s.providerId !== currentUser?.id)
        });
      }
      return {
        previousDetail: previousDetail_2
      };
    };
    $[106] = currentUser?.id;
    $[107] = queryClient;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  let t47;
  if ($[109] !== queryClient) {
    t47 = (__14, taskId_23, context_7) => {
      if (context_7?.previousDetail) {
        queryClient.setQueryData(taskQueryKeys.detail(taskId_23), context_7.previousDetail);
      }
    };
    $[109] = queryClient;
    $[110] = t47;
  } else {
    t47 = $[110];
  }
  let t48;
  if ($[111] !== invalidateTask) {
    t48 = (__15, ___8, taskId_24) => {
      invalidateTask(taskId_24);
    };
    $[111] = invalidateTask;
    $[112] = t48;
  } else {
    t48 = $[112];
  }
  let t49;
  if ($[113] !== t46 || $[114] !== t47 || $[115] !== t48) {
    t49 = {
      mutationFn: _temp15,
      onMutate: t46,
      onError: t47,
      onSettled: t48
    };
    $[113] = t46;
    $[114] = t47;
    $[115] = t48;
    $[116] = t49;
  } else {
    t49 = $[116];
  }
  const t50 = useMutation(t49);
  let t51;
  let t52;
  if ($[117] !== queryClient) {
    t51 = async t53 => {
      const {
        taskId: taskId_26,
        providerIds: providerIds_0
      } = t53;
      await queryClient.cancelQueries({
        queryKey: taskQueryKeys.detail(taskId_26)
      });
      const previousDetail_3 = queryClient.getQueryData(taskQueryKeys.detail(taskId_26));
      if (previousDetail_3) {
        const existingMap = new Map(previousDetail_3.subscribers.map(_temp17));
        const optimisticSubscribers = providerIds_0.map(pid => existingMap.get(pid) ?? {
          id: `temp-${pid}`,
          providerId: pid,
          createdDate: new Date().toISOString()
        });
        queryClient.setQueryData(taskQueryKeys.detail(taskId_26), {
          ...previousDetail_3,
          subscribers: optimisticSubscribers
        });
      }
      return {
        previousDetail: previousDetail_3
      };
    };
    t52 = (__16, variables_7, context_8) => {
      if (context_8?.previousDetail) {
        queryClient.setQueryData(taskQueryKeys.detail(variables_7.taskId), context_8.previousDetail);
      }
    };
    $[117] = queryClient;
    $[118] = t51;
    $[119] = t52;
  } else {
    t51 = $[118];
    t52 = $[119];
  }
  let t53;
  if ($[120] !== invalidateTask || $[121] !== invalidateTasks) {
    t53 = (__17, ___9, variables_8) => {
      invalidateTask(variables_8.taskId);
      invalidateTasks();
    };
    $[120] = invalidateTask;
    $[121] = invalidateTasks;
    $[122] = t53;
  } else {
    t53 = $[122];
  }
  let t54;
  if ($[123] !== t51 || $[124] !== t52 || $[125] !== t53) {
    t54 = {
      mutationFn: _temp16,
      onMutate: t51,
      onError: t52,
      onSettled: t53
    };
    $[123] = t51;
    $[124] = t52;
    $[125] = t53;
    $[126] = t54;
  } else {
    t54 = $[126];
  }
  const t55 = useMutation(t54);
  let t56;
  if ($[127] !== t10 || $[128] !== t15 || $[129] !== t20 || $[130] !== t25 || $[131] !== t30 || $[132] !== t35 || $[133] !== t40 || $[134] !== t45 || $[135] !== t50 || $[136] !== t55) {
    t56 = {
      create: t10,
      update: t15,
      delete: t20,
      complete: t25,
      addComment: t30,
      updateComment: t35,
      deleteComment: t40,
      subscribe: t45,
      unsubscribe: t50,
      setSubscribers: t55
    };
    $[127] = t10;
    $[128] = t15;
    $[129] = t20;
    $[130] = t25;
    $[131] = t30;
    $[132] = t35;
    $[133] = t40;
    $[134] = t45;
    $[135] = t50;
    $[136] = t55;
    $[137] = t56;
  } else {
    t56 = $[137];
  }
  return t56;
};
function _temp4(t0) {
  const [key] = t0;
  return !key.includes("infinite");
}
function _temp5(t0) {
  const [key_0] = t0;
  return key_0.includes("infinite");
}
function _temp6(task) {
  return createTask(task);
}
function _temp7(t0) {
  const {
    taskId: taskId_2,
    task: task_0
  } = t0;
  return updateTask(taskId_2, task_0);
}
function _temp8(taskId_4) {
  return deleteTask(taskId_4);
}
function _temp9(taskId_7) {
  return completeTask(taskId_7);
}
function _temp10(t0) {
  const {
    taskId: taskId_11,
    content,
    files
  } = t0;
  return addComment(taskId_11, content, files);
}
function _temp11(file) {
  return {
    url: "",
    name: file.name
  };
}
function _temp12(t0) {
  const {
    taskId: taskId_13,
    commentId,
    comment
  } = t0;
  return updateComment(taskId_13, commentId, comment);
}
function _temp13(t0) {
  const {
    taskId: taskId_15,
    commentId: commentId_1
  } = t0;
  return deleteComment(taskId_15, commentId_1);
}
function _temp14(taskId_17) {
  return subscribeToTask(taskId_17);
}
function _temp15(taskId_21) {
  return unsubscribeFromTask(taskId_21);
}
function _temp16(t0) {
  const {
    taskId: taskId_25,
    providerIds
  } = t0;
  return setTaskSubscribers(taskId_25, providerIds);
}
function _temp17(s_0) {
  return [s_0.providerId, s_0];
}